import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    VoiceAgentCallCategory,
    VoiceAgentCallListResponse,
    VoiceAgentCallDetail,
    VoiceLiveCall,
} from '../models/VoiceAgent'
import { useUser } from './useUser'

const VOICE_AGENT_KEY = 'voice-agent'
const BASE = '/voice-agent'

export interface CallListFilters {
    search?: string
    date_from?: string
    date_to?: string
    has_transcript?: boolean
    has_recording?: boolean
    min_duration?: number
    max_duration?: number
    caller?: string
    call_topic?: string
    lead_id?: number
    call_category?: VoiceAgentCallCategory
}

export const useVoiceAgentCallList = (
    page = 1,
    pageSize = 25,
    filters: CallListFilters = {},
) => {
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data, isLoading, error, refetch } = useQuery<
        VoiceAgentCallListResponse,
        AxiosError
    >({
        queryKey: [
            VOICE_AGENT_KEY,
            'calls',
            apartmentId,
            page,
            pageSize,
            filters,
        ],
        queryFn: async () => {
            const params: Record<string, string | number | boolean> = {
                page,
                page_size: pageSize,
            }
            if (filters.search) params.search = filters.search
            if (filters.date_from) params.date_from = filters.date_from
            if (filters.date_to) params.date_to = filters.date_to
            if (filters.caller) params.caller = filters.caller
            if (filters.has_transcript !== undefined)
                params.has_transcript = filters.has_transcript
            if (filters.has_recording !== undefined)
                params.has_recording = filters.has_recording
            if (filters.min_duration !== undefined)
                params.min_duration = filters.min_duration
            if (filters.max_duration !== undefined)
                params.max_duration = filters.max_duration
            if (filters.call_topic) params.call_topic = filters.call_topic
            if (filters.lead_id) params.lead_id = filters.lead_id
            if (filters.call_category)
                params.call_category = filters.call_category

            const res = await axiosInstance.get<VoiceAgentCallListResponse>(
                `${BASE}/calls/`,
                { params },
            )
            return res.data
        },
        enabled:
            isApartment &&
            !!apartmentId &&
            (filters.lead_id == null || filters.lead_id > 0),
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    return {
        calls: data?.results ?? [],
        count: data?.count ?? 0,
        hasNext: !!data?.next,
        hasPrevious: !!data?.previous,
        isLoading,
        error,
        refetch,
    }
}

export const useVoiceAgentLiveCalls = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()
    const previousCount = useRef(0)

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data, isLoading } = useQuery<VoiceLiveCall[], AxiosError>({
        queryKey: [VOICE_AGENT_KEY, 'live-calls', apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<{ results: VoiceLiveCall[] }>(
                `${BASE}/calls/live/`,
            )
            return res.data.results ?? []
        },
        enabled: isApartment && !!apartmentId,
        refetchInterval: 5000,
        refetchOnWindowFocus: true,
    })

    const calls = data ?? []

    useEffect(() => {
        if (previousCount.current > 0 && calls.length < previousCount.current) {
            queryClient.invalidateQueries({
                queryKey: [VOICE_AGENT_KEY, 'calls'],
            })
            queryClient.invalidateQueries({
                queryKey: [VOICE_AGENT_KEY, 'questions'],
            })
        }
        previousCount.current = calls.length
    }, [calls.length, queryClient])

    return {
        calls,
        isLoading,
    }
}

export const useVoiceAgentCallDetail = (
    callId: string | undefined,
    poll = false,
) => {
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data, isLoading, error } = useQuery<
        VoiceAgentCallDetail,
        AxiosError
    >({
        queryKey: [VOICE_AGENT_KEY, 'call-detail', callId],
        queryFn: async () => {
            const res = await axiosInstance.get<VoiceAgentCallDetail>(
                `${BASE}/calls/${callId}/`,
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId && !!callId,
        refetchInterval: poll ? 3000 : false,
        refetchOnWindowFocus: false,
    })

    return { call: data, isLoading, error }
}

export const fetchCallRecording = (callId: string, stereo = false) =>
    axiosInstance.get<Blob>(`${BASE}/calls/${callId}/recording/`, {
        responseType: 'blob',
        params: stereo ? { stereo: true } : undefined,
    })
